/**
 * InterviewerSelector Component
 * 
 * Card-based multi-select for interviewers with search functionality
 */

import React, { useEffect, useState, useMemo } from 'react';
import { Card, Avatar, Typography, Input, Empty, Spin, Flex } from 'antd';
import { CheckCircleOutlined, UserOutlined } from '@ant-design/icons';
import { Interviewer } from '../../../../../Entities/JobDescription';
import teamMemberService from '../../../../../Service/team-member.service';
import AuthService from '../../../../../Service/auth.service';

const { Text, Paragraph } = Typography;
const { Search } = Input;

interface InterviewerSelectorProps {
  value?: Interviewer[];
  onChange?: (interviewers: Interviewer[]) => void;
  required?: boolean;
}

const InterviewerSelector: React.FC<InterviewerSelectorProps> = ({
  value = [],
  onChange,
  required = false,
}) => {
  const [availableInterviewers, setAvailableInterviewers] = useState<Interviewer[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');

  // Fetch available interviewers
  useEffect(() => {
    const fetchInterviewers = async () => {
      try {
        setLoading(true);
        const response = await teamMemberService.fetchAvailableTeamMembersForInterViews();
        setAvailableInterviewers(response?.availableMembers || []);
      } catch (error) {
        console.error('Failed to fetch interviewers:', error);
        setAvailableInterviewers([]);
      } finally {
        setLoading(false);
      }
    };

    fetchInterviewers();
  }, []);

  // Filter interviewers based on search
  const filteredInterviewers = useMemo(() => {
    if (!searchText.trim()) {
      return availableInterviewers;
    }

    const normalizedSearch = searchText.toLowerCase();
    return availableInterviewers.filter((interviewer) => {
      const fullName = `${interviewer.firstName} ${interviewer.lastName}`.toLowerCase();
      const email = interviewer.email?.toLowerCase() || '';
      return fullName.includes(normalizedSearch) || email.includes(normalizedSearch);
    });
  }, [availableInterviewers, searchText]);

  // Pre-select admin users and current user on mount if no selection
  useEffect(() => {
    if (value.length === 0 && availableInterviewers.length > 0) {
      const adminUsers = availableInterviewers.filter((member) => member.role === 'admin');
      const currentUser = AuthService.getInstance().getCurrentUser();
      
      const defaultInterviewers: Interviewer[] = [];
      
      // Add admin users
      adminUsers.forEach((admin) => {
        defaultInterviewers.push(admin);
      });
      
      // Add current user if not already included
      if (currentUser) {
        const currentUserInterviewer = availableInterviewers.find(
          (member) => member._id === currentUser._id
        );
        if (currentUserInterviewer && !defaultInterviewers.some(i => i._id === currentUserInterviewer._id)) {
          defaultInterviewers.push(currentUserInterviewer);
        }
      }
      
      if (defaultInterviewers.length > 0) {
        onChange?.(defaultInterviewers);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableInterviewers]);

  const handleToggleSelection = (interviewer: Interviewer) => {
    const isSelected = value.some((inv) => inv._id === interviewer._id);
    
    if (isSelected) {
      // Remove from selection
      onChange?.(value.filter((inv) => inv._id !== interviewer._id));
    } else {
      // Add to selection
      onChange?.([...value, interviewer]);
    }
  };

  const getInitials = (firstName: string, lastName: string): string => {
    const firstInitial = firstName?.[0]?.toUpperCase() || '';
    const lastInitial = lastName?.[0]?.toUpperCase() || '';
    return `${firstInitial}${lastInitial}`;
  };

  if (loading) {
    return (
      <Flex justify="center" align="center" style={{ padding: 40 }}>
        <Spin size="large" />
      </Flex>
    );
  }

  if (availableInterviewers.length === 0) {
    return (
      <Empty
        description="No interviewers available"
        image={Empty.PRESENTED_IMAGE_SIMPLE}
      />
    );
  }

  return (
    <div className="interviewer-selector">
      <Search
        placeholder="Search interviewers by name or email"
        allowClear
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        style={{ marginBottom: 16 }}
      />

      {filteredInterviewers.length === 0 ? (
        <Empty
          description="No interviewers found matching your search"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
            gap: 12,
            maxHeight: '400px',
            overflowY: 'auto',
          }}
        >
          {filteredInterviewers.map((interviewer) => {
            const isSelected = value.some((inv) => inv._id === interviewer._id);
            const fullName = `${interviewer.firstName} ${interviewer.lastName}`;

            return (
              <Card
                key={interviewer._id}
                hoverable
                onClick={() => handleToggleSelection(interviewer)}
                style={{
                  cursor: 'pointer',
                  borderColor: isSelected ? 'var(--ant-color-primary)' : undefined,
                  borderWidth: isSelected ? 2 : 1,
                  backgroundColor: isSelected ? 'var(--ant-color-primary-bg)' : undefined,
                }}
                bodyStyle={{ padding: 12 }}
              >
                <Flex align="center" gap={12}>
                  <Avatar
                    size={40}
                    icon={<UserOutlined />}
                    style={{ backgroundColor: isSelected ? 'var(--ant-color-primary)' : '#d9d9d9' }}
                  >
                    {getInitials(interviewer.firstName, interviewer.lastName)}
                  </Avatar>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Flex align="center" gap={4}>
                      <Text strong style={{ fontSize: 14 }}>
                        {fullName}
                      </Text>
                      {isSelected && (
                        <CheckCircleOutlined
                          style={{ color: 'var(--ant-color-primary)', fontSize: 16 }}
                        />
                      )}
                    </Flex>
                    <Paragraph
                      ellipsis
                      style={{ margin: 0, fontSize: 12, color: 'var(--ant-color-text-secondary)' }}
                    >
                      {interviewer.email}
                    </Paragraph>
                    {interviewer.role && (
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        {interviewer.role}
                      </Text>
                    )}
                  </div>
                </Flex>
              </Card>
            );
          })}
        </div>
      )}

      {required && value.length === 0 && (
        <Text type="danger" style={{ fontSize: 12, marginTop: 8, display: 'block' }}>
          Please select at least one interviewer
        </Text>
      )}
    </div>
  );
};

export default InterviewerSelector;

